/**
 * journeyMilestones
 *
 * Which of a journey rung's four sub-lines are actually done.
 *
 * They used to be checked a rung at a time: every line of a past phase carried a
 * tick, every line of the current one carried none, whether or not she had done
 * it. So the ticks said only "this rung is behind you" — which the rail already
 * said — and the current rung, the one she is living, never showed progress at
 * all.
 *
 * Every line here answers to something the app can actually observe. That is a
 * constraint on the copy, not just on the code: a line no data can confirm gets
 * rewritten or it does not belong on the list. A tick that is wrong on a care
 * journey is worse than no tick.
 */

/** What the app can see. Each field is one observable fact, nothing derived. */
export type JourneySignals = {
  /** She has told us what she is here for. */
  hasGoal: boolean;
  /** Any document or test result on file. */
  hasDocument: boolean;
  /** Any appointment booked, ever. */
  hasAppointment: boolean;
  /** At least one care-team member — her clinic, usually. */
  hasCareTeam: boolean;
  /** Any lab result on file. */
  hasTestResult: boolean;
  /** A medication attached to the cycle. */
  hasMedication: boolean;
  /** A dose actually logged, as opposed to a prescription recorded. */
  hasMedicationLog: boolean;
  /** A partner account is linked. */
  partnerConnected: boolean;
  /** A monitoring encounter on the cycle. */
  hasMonitoring: boolean;
  /** A retrieval, transfer or insemination that has happened. */
  hasPerformedProcedure: boolean;
  /** The luteal phase has started. */
  hasLutealPhase: boolean;
  /** Something in the vault. */
  hasSpecimen: boolean;
  /** A specimen whose storage location names a facility. */
  hasStorageFacility: boolean;
  /** A cycle started after the one this map is describing. */
  hasLaterCycle: boolean;
  /** An outcome is recorded on the cycle. */
  hasRecordedOutcome: boolean;
};

export const NO_JOURNEY_SIGNALS: JourneySignals = {
  hasGoal: false,
  hasDocument: false,
  hasAppointment: false,
  hasCareTeam: false,
  hasTestResult: false,
  hasMedication: false,
  hasMedicationLog: false,
  partnerConnected: false,
  hasMonitoring: false,
  hasPerformedProcedure: false,
  hasLutealPhase: false,
  hasSpecimen: false,
  hasStorageFacility: false,
  hasLaterCycle: false,
  hasRecordedOutcome: false,
};

/**
 * The signal behind each line, in the order the lines are rendered.
 *
 * Keyed by the copy prefix rather than the rung id, because the last rung has
 * two sets of copy — `storing` on a preservation path, `result` on a pregnancy
 * one — and they do not check for the same reasons.
 *
 * Two lines may share a signal (a clinic in the care team answers both "add your
 * clinic" and "set up your care team"). That is deliberate: the lines are worth
 * saying separately even where one fact settles both.
 */
const MILESTONE_SIGNALS: Record<string, (keyof JourneySignals)[]> = {
  deciding: ["hasGoal", "hasDocument", "hasAppointment", "hasCareTeam"],
  preparing: [
    "hasTestResult",
    "hasMedication",
    "hasCareTeam",
    "partnerConnected",
  ],
  in_treatment: [
    "hasMonitoring",
    "hasMedicationLog",
    "hasPerformedProcedure",
    "hasLutealPhase",
  ],
  storing: [
    "hasStorageFacility",
    "hasSpecimen",
    "hasDocument",
    "hasLaterCycle",
  ],
  result: [
    "hasMedicationLog",
    "hasRecordedOutcome",
    "hasDocument",
    "hasLaterCycle",
  ],
};

/**
 * Whether one line is done.
 *
 * Unknown rung or line index answers false rather than throwing: a map that
 * loses a tick is a smaller failure than a profile screen that will not render.
 */
export function isMilestoneDone(
  phaseKey: string,
  milestoneIndex: number,
  signals: JourneySignals,
): boolean {
  const signal = MILESTONE_SIGNALS[phaseKey]?.[milestoneIndex];
  return signal ? signals[signal] : false;
}

/** The rung's own progress, for callers that want to summarise it. */
export function countMilestonesDone(
  phaseKey: string,
  signals: JourneySignals,
): { done: number; total: number } {
  const keys = MILESTONE_SIGNALS[phaseKey] ?? [];
  return {
    done: keys.filter((key) => signals[key]).length,
    total: keys.length,
  };
}
